import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

type WorkspaceType = "personal" | "groups";

interface WorkspaceContextType {
  workspace: WorkspaceType;
  setWorkspace: (workspace: WorkspaceType) => void;
  selectedGroupId: string | null;
  setSelectedGroupId: (groupId: string | null) => void;
  isGroupSwitching: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getWorkspaceFromPath(pathname: string): { workspace: WorkspaceType; groupId: string | null } | null {
  const pathParts = pathname.split("/").filter(Boolean);
  const appIndex = pathParts.findIndex((part) => part === "dashboard");

  if (appIndex === -1) {
    return null;
  }

  const scope = pathParts[appIndex + 1];

  if (scope === "groups") {
    const potentialGroupId = pathParts[appIndex + 2];
    return {
      workspace: "groups",
      groupId: potentialGroupId && UUID_REGEX.test(potentialGroupId) ? potentialGroupId : null,
    };
  }

  if (scope === "personal") {
    return { workspace: "personal", groupId: null };
  }

  return null;
}

export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { user } = useAuth();

  const initialWorkspaceState = getWorkspaceFromPath(location.pathname);
  const [workspace, setWorkspace] = useState<WorkspaceType>(initialWorkspaceState?.workspace ?? "personal");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(initialWorkspaceState?.groupId ?? null);
  const [isGroupSwitching, setIsGroupSwitching] = useState(false);

  // Keep workspace context synced to URL (supports refresh/deep-links)
  useEffect(() => {
    const routeState = getWorkspaceFromPath(location.pathname);
    if (!routeState) return;

    setWorkspace((prev) => (prev === routeState.workspace ? prev : routeState.workspace));
    setSelectedGroupId((prev) => (prev === routeState.groupId ? prev : routeState.groupId));

    if (user && ((routeState.workspace === "groups" && routeState.groupId) || routeState.workspace === "personal")) {
      localStorage.setItem(`last_dashboard_workspace:${user.id}`, location.pathname);
    }
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (workspace === "groups" && selectedGroupId) {
      setIsGroupSwitching(true);
      const timer = setTimeout(() => {
        setIsGroupSwitching(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [selectedGroupId, workspace]);

  return (
    <WorkspaceContext.Provider
      value={{ workspace, setWorkspace, selectedGroupId, setSelectedGroupId, isGroupSwitching }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};
